import React, { useEffect, useMemo, useState } from "react";

import { Check } from "@phosphor-icons/react";
import { toast } from "sonner";

import ActaModal from "@/components/ui/ActaModal";


const EQUIPMENT_TYPES = [
  { value: "machine", label: "Machine" },
  { value: "accessory", label: "Accessoire" },
  { value: "handpiece", label: "Pièce à main" },
];


const MACHINE_STATUSES = [
  { value: "active", label: "Opérationnelle" },
  { value: "maintenance", label: "En maintenance" },
  { value: "inactive", label: "Inactive" },
  { value: "retired", label: "Hors service" },
];


const LOCATION_STATUSES = [
  { value: "in_stock", label: "En stock" },
  { value: "reserved", label: "Réservée" },
  { value: "installed", label: "Installée chez un client" },
  { value: "returned", label: "Retournée" },
];


function normalizeDate(value) {
  return value
    ? String(value).slice(0, 10)
    : "";
}


function prospectLabel(prospect) {
  const contactName = [
    prospect?.firstName,
    prospect?.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    prospect?.company ||
    contactName ||
    prospect?.email ||
    prospect?.id ||
    "Client"
  );
}


function formFromMachine(machine) {
  return {
    prospect_id: machine?.prospectId || "",
    equipment_type:
      machine?.equipmentType || "machine",
    location_status:
      machine?.locationStatus ||
      (
        machine?.prospectId
          ? "installed"
          : "in_stock"
      ),
    stock_location:
      machine?.stockLocation || "",
    stock_received_at:
      normalizeDate(machine?.stockReceivedAt),
    name: machine?.name || "",
    serial_number:
      machine?.serialNumber || "",
    manufacturer:
      machine?.manufacturer || "",
    model_name:
      machine?.model || "",
    status:
      machine?.status || "active",
    site_name:
      machine?.siteName || "",
    address:
      machine?.address || "",
    city:
      machine?.city || "",
    postal_code:
      machine?.postalCode || "",
    country:
      machine?.country || "FR",
    commissioning_date:
      normalizeDate(
        machine?.commissioningDate
      ),
    warranty_end_date:
      normalizeDate(
        machine?.warrantyEndDate
      ),
    notes:
      machine?.notes || "",
  };
}


export default function MachineIdentityEditModal({
  open,
  machine,
  prospects = [],
  saving = false,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(
    () => formFromMachine(machine)
  );


  useEffect(() => {
    if (open) {
      setForm(
        formFromMachine(machine)
      );
    }
  }, [
    machine,
    open,
  ]);


  const sortedProspects = useMemo(
    () =>
      [...prospects].sort(
        (left, right) =>
          prospectLabel(left).localeCompare(
            prospectLabel(right),
            "fr",
            {
              sensitivity: "base",
            }
          )
      ),
    [prospects]
  );


  if (!open || !machine) {
    return null;
  }


  const catalogIdentityLocked =
    Boolean(machine.machineCatalogId);

  const requiresClient =
    [
      "reserved",
      "installed",
    ].includes(
      form.location_status
    );

  const usesStockLocation =
    form.location_status !==
    "installed";


  const updateField = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };


  const updateLocationStatus = (
    value
  ) => {
    setForm((current) => ({
      ...current,
      location_status: value,
      prospect_id:
        [
          "in_stock",
          "returned",
        ].includes(value)
          ? ""
          : current.prospect_id,
    }));
  };


  const submit = (
    event
  ) => {
    event.preventDefault();

    const name =
      form.name.trim();

    const serialNumber =
      form.serial_number.trim();

    const prospectId =
      form.prospect_id.trim() ||
      null;

    if (!name) {
      toast.error(
        "Le nom de l'équipement est obligatoire."
      );
      return;
    }

    if (!serialNumber) {
      toast.error(
        "Le numéro de série est obligatoire."
      );
      return;
    }

    if (
      requiresClient &&
      !prospectId
    ) {
      toast.error(
        "Sélectionnez le client lié à cet équipement."
      );
      return;
    }

    if (
      form.commissioning_date &&
      form.warranty_end_date &&
      form.warranty_end_date <
        form.commissioning_date
    ) {
      toast.error(
        "La fin de garantie ne peut pas être antérieure à la mise en service."
      );
      return;
    }

    onSubmit({
      prospect_id:
        [
          "in_stock",
          "returned",
        ].includes(
          form.location_status
        )
          ? null
          : prospectId,
      equipment_type:
        form.equipment_type,
      location_status:
        form.location_status,
      stock_location:
        form.stock_location.trim(),
      stock_received_at:
        form.stock_received_at ||
        null,
      name,
      serial_number:
        serialNumber,
      manufacturer:
        form.manufacturer.trim(),
      model_name:
        form.model_name.trim(),
      status:
        form.status,
      site_name:
        form.site_name.trim(),
      address:
        form.address.trim(),
      city:
        form.city.trim(),
      postal_code:
        form.postal_code.trim(),
      country:
        form.country || "FR",
      commissioning_date:
        form.commissioning_date ||
        null,
      warranty_end_date:
        form.warranty_end_date ||
        null,
      notes:
        form.notes.trim(),
    });
  };


  const inputClass =
    "w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm outline-none transition focus:border-[var(--do-accent)] disabled:cursor-not-allowed disabled:opacity-60";


  const footer = (
    <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <button
        type="button"
        onClick={onClose}
        disabled={saving}
        className="rounded-lg border border-[var(--do-border)] px-4 py-2.5 text-sm font-heading font-semibold transition hover:bg-[var(--do-surface-soft)] disabled:opacity-50"
      >
        Annuler
      </button>

      <button
        type="submit"
        form="machine-identity-edit-form"
        disabled={saving}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--do-accent)] px-4 py-2.5 text-sm font-heading font-bold text-[var(--do-on-accent)] transition hover:opacity-90 disabled:opacity-50"
      >
        <Check
          size={18}
          weight="bold"
        />
        {saving
          ? "Enregistrement…"
          : "Enregistrer"}
      </button>
    </div>
  );


  return (
    <ActaModal
      open={open}
      onClose={onClose}
      eyebrow="GMAO"
      title="Modifier la machine"
      size="xl"
      closeDisabled={saving}
      footer={footer}
      testId="machine-identity-edit-modal"
    >
      <form
        id="machine-identity-edit-form"
        onSubmit={submit}
        className="space-y-6"
      >
        <section>
          <h3 className="font-heading text-sm font-black uppercase tracking-wide text-[var(--do-muted)]">
            Identité
          </h3>

          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="machine-edit-type"
                className="label-eyebrow mb-1.5 block"
              >
                Type
              </label>

              <select
                id="machine-edit-type"
                value={form.equipment_type}
                onChange={(event) =>
                  updateField(
                    "equipment_type",
                    event.target.value
                  )
                }
                disabled={
                  saving ||
                  catalogIdentityLocked
                }
                className={inputClass}
              >
                {EQUIPMENT_TYPES.map(
                  (item) => (
                    <option
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </option>
                  )
                )}
              </select>
            </div>

            <div>
              <label
                htmlFor="machine-edit-status"
                className="label-eyebrow mb-1.5 block"
              >
                État technique
              </label>

              <select
                id="machine-edit-status"
                value={form.status}
                onChange={(event) =>
                  updateField(
                    "status",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              >
                {MACHINE_STATUSES.map(
                  (item) => (
                    <option
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </option>
                  )
                )}
              </select>
            </div>

            <div>
              <label
                htmlFor="machine-edit-name"
                className="label-eyebrow mb-1.5 block"
              >
                Nom *
              </label>

              <input
                id="machine-edit-name"
                value={form.name}
                onChange={(event) =>
                  updateField(
                    "name",
                    event.target.value
                  )
                }
                disabled={
                  saving ||
                  catalogIdentityLocked
                }
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="machine-edit-serial"
                className="label-eyebrow mb-1.5 block"
              >
                Numéro de série *
              </label>

              <input
                id="machine-edit-serial"
                value={
                  form.serial_number
                }
                onChange={(event) =>
                  updateField(
                    "serial_number",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="machine-edit-manufacturer"
                className="label-eyebrow mb-1.5 block"
              >
                Fabricant
              </label>

              <input
                id="machine-edit-manufacturer"
                value={
                  form.manufacturer
                }
                onChange={(event) =>
                  updateField(
                    "manufacturer",
                    event.target.value
                  )
                }
                disabled={
                  saving ||
                  catalogIdentityLocked
                }
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="machine-edit-model"
                className="label-eyebrow mb-1.5 block"
              >
                Modèle
              </label>

              <input
                id="machine-edit-model"
                value={form.model_name}
                onChange={(event) =>
                  updateField(
                    "model_name",
                    event.target.value
                  )
                }
                disabled={
                  saving ||
                  catalogIdentityLocked
                }
                className={inputClass}
              />
            </div>
          </div>

          {catalogIdentityLocked && (
            <p className="mt-2 text-xs text-[var(--do-muted)]">
              Le nom, le type, le fabricant et le modèle proviennent du catalogue et restent verrouillés.
            </p>
          )}
        </section>

        <section>
          <h3 className="font-heading text-sm font-black uppercase tracking-wide text-[var(--do-muted)]">
            Affectation
          </h3>

          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="machine-edit-location-status"
                className="label-eyebrow mb-1.5 block"
              >
                Situation
              </label>

              <select
                id="machine-edit-location-status"
                value={
                  form.location_status
                }
                onChange={(event) =>
                  updateLocationStatus(
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              >
                {LOCATION_STATUSES.map(
                  (item) => (
                    <option
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </option>
                  )
                )}
              </select>
            </div>

            {![
              "in_stock",
              "returned",
            ].includes(
              form.location_status
            ) && (
              <div>
                <label
                  htmlFor="machine-edit-client"
                  className="label-eyebrow mb-1.5 block"
                >
                  Client
                  {requiresClient
                    ? " *"
                    : ""}
                </label>

                <select
                  id="machine-edit-client"
                  value={
                    form.prospect_id
                  }
                  onChange={(event) =>
                    updateField(
                      "prospect_id",
                      event.target.value
                    )
                  }
                  disabled={saving}
                  className={inputClass}
                >
                  <option value="">
                    Sélectionner le client
                  </option>

                  {sortedProspects.map(
                    (prospect) => (
                      <option
                        key={prospect.id}
                        value={prospect.id}
                      >
                        {prospectLabel(
                          prospect
                        )}
                      </option>
                    )
                  )}
                </select>
              </div>
            )}

            {usesStockLocation && (
              <>
                <div>
                  <label
                    htmlFor="machine-edit-stock-location"
                    className="label-eyebrow mb-1.5 block"
                  >
                    Emplacement de stockage
                  </label>

                  <input
                    id="machine-edit-stock-location"
                    value={
                      form.stock_location
                    }
                    onChange={(event) =>
                      updateField(
                        "stock_location",
                        event.target.value
                      )
                    }
                    disabled={saving}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label
                    htmlFor="machine-edit-stock-date"
                    className="label-eyebrow mb-1.5 block"
                  >
                    Date d'entrée en stock
                  </label>

                  <input
                    id="machine-edit-stock-date"
                    type="date"
                    value={
                      form.stock_received_at
                    }
                    onChange={(event) =>
                      updateField(
                        "stock_received_at",
                        event.target.value
                      )
                    }
                    disabled={saving}
                    className={inputClass}
                  />
                </div>
              </>
            )}
          </div>
        </section>

        <section>
          <h3 className="font-heading text-sm font-black uppercase tracking-wide text-[var(--do-muted)]">
            Implantation et cycle de vie
          </h3>

          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="machine-edit-site"
                className="label-eyebrow mb-1.5 block"
              >
                Site
              </label>
              <input
                id="machine-edit-site"
                value={form.site_name}
                onChange={(event) =>
                  updateField(
                    "site_name",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="machine-edit-address"
                className="label-eyebrow mb-1.5 block"
              >
                Adresse
              </label>
              <input
                id="machine-edit-address"
                value={form.address}
                onChange={(event) =>
                  updateField(
                    "address",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="machine-edit-postal"
                className="label-eyebrow mb-1.5 block"
              >
                Code postal
              </label>
              <input
                id="machine-edit-postal"
                value={form.postal_code}
                onChange={(event) =>
                  updateField(
                    "postal_code",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="machine-edit-city"
                className="label-eyebrow mb-1.5 block"
              >
                Ville
              </label>
              <input
                id="machine-edit-city"
                value={form.city}
                onChange={(event) =>
                  updateField(
                    "city",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="machine-edit-country"
                className="label-eyebrow mb-1.5 block"
              >
                Pays
              </label>
              <select
                id="machine-edit-country"
                value={form.country}
                onChange={(event) =>
                  updateField(
                    "country",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              >
                <option value="FR">
                  France
                </option>
                <option value="BE">
                  Belgique
                </option>
              </select>
            </div>

            <div>
              <label
                htmlFor="machine-edit-commissioning"
                className="label-eyebrow mb-1.5 block"
              >
                Mise en service
              </label>
              <input
                id="machine-edit-commissioning"
                type="date"
                value={
                  form.commissioning_date
                }
                onChange={(event) =>
                  updateField(
                    "commissioning_date",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="machine-edit-warranty"
                className="label-eyebrow mb-1.5 block"
              >
                Fin de garantie
              </label>
              <input
                id="machine-edit-warranty"
                type="date"
                value={
                  form.warranty_end_date
                }
                onChange={(event) =>
                  updateField(
                    "warranty_end_date",
                    event.target.value
                  )
                }
                disabled={saving}
                className={inputClass}
              />
            </div>
          </div>

          <div className="mt-4">
            <label
              htmlFor="machine-edit-notes"
              className="label-eyebrow mb-1.5 block"
            >
              Notes
            </label>

            <textarea
              id="machine-edit-notes"
              rows={4}
              value={form.notes}
              onChange={(event) =>
                updateField(
                  "notes",
                  event.target.value
                )
              }
              disabled={saving}
              className={inputClass}
            />
          </div>
        </section>
      </form>
    </ActaModal>
  );
}
